import React, {useRef, useState} from 'react'
import {Button, Card, H3, H5, IconButton, ListItem, P, Popup, Row, SortableList, Panel} from '../../../../components/UI/index.js'
import {formSpacing, FormStack, Input, TextArea, Toggle} from '../../../../components/UI/forms/index.js'
import {defaultCustomerSupportModalText, defaultCustomerSupportModalTitle, getCustomerSupportModalText, getCustomerSupportModalTitle} from '../../../../components/parcels/CustomerSupportModal.js'
import {Reseller, SupportReason} from '../../../../../server/functions/resellers/resellers.types.js'
import guid from '../../../../../utils/guid.js'
import Languages from '../../../../../utils/Languages.js'
import type {PopupRef} from '../../../../components/UI/overlays/Popup.js'

interface CustomerSupportProps {
    reseller: Reseller
    onChange: (reseller: Reseller, path: string) => void
}

export default function CustomerSupport({reseller, onChange}: CustomerSupportProps) {
    const popupRef = useRef<PopupRef>(null)

    // Local state so a cleared field shows the default text again after saving
    const [modalTitle, setModalTitle] = useState(() => getCustomerSupportModalTitle(reseller))
    const [modalText, setModalText] = useState(() => getCustomerSupportModalText(reseller))

    const addSupportReason = () => {
        const newSupportReason: Reseller['settings']['parcels']['customerSupport']['supportReasons'][0] = {
            id: guid.generate(),
            name: ''
        }

        reseller.settings.parcels.customerSupport.supportReasons.push(newSupportReason)
        onChange(reseller, 'parcels.customerSupport')
    }

    const removeSupportReason = (event: React.MouseEvent, supportReason: Reseller['settings']['parcels']['customerSupport']['supportReasons'][0]) => {
        event.stopPropagation()

        popupRef.current?.open('Reden verwijderen', 'Weet je zeker dat je deze reden wilt verwijderen?', () => {
            const index = reseller.settings.parcels.customerSupport.supportReasons.findIndex((sr) => sr.id === supportReason.id)

            if (index !== -1) {
                reseller.settings.parcels.customerSupport.supportReasons.splice(index, 1)
                onChange(reseller, 'parcels.customerSupport')
            }

            popupRef.current?.close()
        })
    }

    return (
        <Row style={{gap: 24, justifyContent: 'flex-start'}}>
            <Card shadow style={{display: 'flex', flexDirection: 'column', padding: 24, width: 600, height: 'fit-content'}}>
                <H3 style={{marginBottom: 36}}>{Languages.customerSupport['NL']}</H3>

                <P style={{marginBottom: 24}}>{Languages.customerSupportDescription['NL']}</P>

                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <Toggle
                        label={Languages.enabled['NL']}
                        checked={reseller.settings.parcels.customerSupport.enabled}
                        onChange={(event) => {
                            reseller.settings.parcels.customerSupport = {...reseller.settings.parcels.customerSupport, enabled: event.target.checked}
                            onChange(reseller, 'parcels.customerSupport')
                        }}
                    />

                    <Input
                        style={{marginTop: 12}}
                        label={Languages.emailAddressForCustomerSupport['NL']}
                        value={reseller.settings.parcels.customerSupport.email}
                        onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                            reseller.settings.parcels.customerSupport.email = event.target.value
                            onChange(reseller, 'parcels.customerSupport')
                        }}
                    />

                    <Input
                        label={Languages.customerSupportModalTitleSetting['NL']}
                        value={modalTitle}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setModalTitle(event.target.value)}
                        onBlur={() => {
                            // Store the default as empty, so it keeps following the default text
                            reseller.settings.parcels.customerSupport.modalTitle = modalTitle.trim() === defaultCustomerSupportModalTitle() ? '' : modalTitle.trim()
                            onChange(reseller, 'parcels.customerSupport')
                            setModalTitle(getCustomerSupportModalTitle(reseller))
                        }}
                    />

                    <TextArea
                        label={Languages.customerSupportModalTextSetting['NL']}
                        rows={3}
                        value={modalText}
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setModalText(event.target.value)}
                        onBlur={() => {
                            reseller.settings.parcels.customerSupport.modalText = modalText.trim() === defaultCustomerSupportModalText(reseller) ? '' : modalText.trim()
                            onChange(reseller, 'parcels.customerSupport')
                            setModalText(getCustomerSupportModalText(reseller))
                        }}
                    />
                </FormStack>

                <H5 style={{marginBottom: 24, marginTop: 36}}>{Languages.customerSupportReasons['NL']}</H5>

                {reseller.settings.parcels.customerSupport.supportReasons.length > 0 &&
                    <Panel style={{flex: 1, overflowY: 'auto', maxHeight: 500}}>
                        <SortableList
                            items={reseller.settings.parcels.customerSupport.supportReasons || []}
                            onChange={(supportReasons: SupportReason[]) => {
                                reseller.settings.parcels.customerSupport = {...reseller.settings.parcels.customerSupport, supportReasons}
                                onChange(reseller, 'parcels.customerSupport')
                            }}
                            renderItem={(supportReason: SupportReason) => {
                                return (
                                    <ListItem
                                        icon='mdi mdi-drag-horizontal'
                                    >
                                        <Input
                                            value={supportReason.name}
                                            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                supportReason.name = event.target.value
                                                onChange(reseller, 'parcels.customerSupport')
                                            }}
                                            onKeyDown={(e) => {
                                                e.stopPropagation() // Prevent sortable list from triggering drag and blocking keyboard input
                                            }}
                                        />

                                        <IconButton
                                            style={{marginLeft: 12}}
                                            icon='mdi mdi-delete'
                                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => removeSupportReason(event, supportReason)}
                                        />
                                    </ListItem>
                                )
                            }}
                        />
                    </Panel>
                }

                <Button
                    style={{marginTop: 24}}
                    variant='outline'
                    icon='mdi mdi-plus'
                    label={Languages.addSupportReason['NL']}
                    onClick={addSupportReason}
                />
            </Card>

            <Popup ref={popupRef} />
        </Row>
    )
}
